import Dinero from 'dinero.js';
import type { Dinero as DineroType } from 'dinero.js';
import { formatCurrency } from '@/data/api/products/helpers';
import { instance } from '@/data/api/products/axios';
import { AxiosResponse } from 'axios';

import PromiseCache from '@/helpers/promisecache';

export enum Currency {
  SEK = 'SEK',
  NOK = 'NOK',
  EUR = 'EUR',
}

export interface Price {
  netPrice: number;
  salesPrice: number;
  discount: number;
  lowestQuantityLimitBasicU_M: number;
  salesPriceUnitOfMeasure: string;
  netPriceUnitOfMeasure: string; // Internal field added by our API
  currency: string;
  priceList: string;
  priceListOrigin: string;
  blanketAgreementNumber: string;
  price: number;
  price_total: number;
}
export interface LinePrice {
  lineAmount: number;
  salesPrice: number;
  currency: string;
  priceList: string;
  orderDate: string;
  salesPriceUnitOfMeasure: string;
  salesPriceQuantity: string;
  priceUnitOfMeasure: string; // Internal field added by our API
  priceQuantity: string; // Internal field added by our API
  warehouse: string;
  statusDiscount1: string;
  statusDiscount2: string;
  statusDiscount3: string;
  statusDiscount4: string;
  statusDiscount5: string;
  statusDiscount6: string;
  discountText1: string;
  discountText2: string;
  discountText3: string;
  discountText4: string;
  discountText5: string;
  discountText6: string;
  discount1: string;
  discount2: string;
  discount3: string;
  discount4: string;
  discount5: string;
  discount6: string;
}

export enum PriceType {
  Sales = 'sales',
  Net = 'net',
  M2 = 'm2',
  Line = 'line',
}

export interface PriceObject {
  sales: Price | null;
  net: Price | null;
  m2: LinePrice | null;
  line: LinePrice | null;
}

export interface PriceAttributes {
  artnr: string;
  customer: string;
  warehouse: string;
  region: string;
  quantity: number;
  price_quantity?: number;
  unit?: string;
  address?: string;
  unique?: string; // Generally not used, but can be used to differentiate between different price collections
  salespriceUnit?: string;
}

// We store prices in a map to avoid issues with cloning the PriceManager while it's fetching prices
// Note: This will cause a memory leak as the map isn't cleared until a page refresh. But we should be fine for now.
class PriceStore {
  private prices: Map<
    string,
    {
      TTL: number;
      value: PriceObject;
    }
  >;
  private scheduledForDeletion: Set<string>;

  constructor() {
    this.prices = new Map();
    this.scheduledForDeletion = new Set();

    setInterval(() => {
      this.scheduledForDeletion.forEach((key) => {
        // console.debug('Deleting key', key);
        this.prices.delete(key);
      });
      this.scheduledForDeletion.clear();
    }, 60 * 1000);
  }

  set(key: string, value: PriceObject): void {
    this.scheduledForDeletion.delete(key);
    this.prices.set(key, {
      TTL: Date.now(),
      value,
    });
  }

  get(key: string): PriceObject | undefined {
    this.scheduledForDeletion.delete(key);
    return this.prices.get(key)?.value;
  }

  delete(key: string): void {
    // Check if we should delete immediately to prevent stale data
    if (Date.now() - this.prices.get(key)!.TTL > 120 * 1000) {
      // console.debug('Deleting key immediately', key);
      this.prices.delete(key);
    } else {
      this.scheduledForDeletion.add(key);
    }
  }

  has(key: string): boolean {
    return this.prices.has(key);
  }

  clear(): void {
    this.prices.clear();
  }
}
declare global {
  interface Window {
    _priceManagerPriceStore: PriceStore;
  }
}

if (!window._priceManagerPriceStore) {
  window._priceManagerPriceStore = new PriceStore();
}

export class PriceManager {
  private _fetching: Promise<AxiosResponse<any, any>> | null = null;
  private attributes: PriceAttributes = {
    artnr: '',
    address: '',
    customer: '',
    warehouse: '',
    region: '',
    unit: '',
    quantity: 0,
    salespriceUnit: '',
  };
  private get key(): string {
    return `${this.artnr}:${this.address ?? ''}:${this.customer}:${this.warehouse}:${this.region}:${this.price_quantity ?? ''}:${this.quantity}:${this.unit}:${this.attributes.unique ?? ''}:${this.salespriceUnit ?? ''}`;
  }
  private set prices(value: PriceObject) {
    window._priceManagerPriceStore.set(this.key, value);
  }
  private get prices(): PriceObject {
    const prices = window._priceManagerPriceStore.get(this.key);
    if (prices) {
      return prices;
    }

    window._priceManagerPriceStore.set(this.key, {
      sales: null,
      net: null,
      m2: null,
      line: null,
    });

    return window._priceManagerPriceStore.get(this.key)!;
  }

  awaitFetching(): Promise<void> {
    if (this._fetching) {
      return this._fetching.then(() => {});
    } else {
      return Promise.resolve();
    }
  }

  get loading(): boolean {
    return this._fetching !== null;
  }

  set artnr(value: string) {
    this.attributes.artnr = value;
  }
  get artnr(): string {
    return this.attributes.artnr;
  }

  set address(value: string) {
    this.attributes.address = value;
  }
  get address(): string | undefined {
    return this.attributes.address;
  }

  set customer(value: string) {
    this.attributes.customer = value;
  }
  get customer(): string {
    return this.attributes.customer;
  }

  set warehouse(value: string) {
    this.attributes.warehouse = value;
  }
  get warehouse(): string {
    return this.attributes.warehouse;
  }

  set region(value: string) {
    this.attributes.region = value;
  }
  get region(): string {
    return this.attributes.region;
  }

  set quantity(value: number) {
    this.attributes.quantity = value;
  }
  get quantity(): number {
    return this.attributes.quantity;
  }

  set(price_quantity: number) {
    this.attributes.price_quantity = price_quantity;
  }
  get price_quantity(): number | undefined {
    return this.attributes.price_quantity;
  }

  set unit(value: string) {
    this.attributes.unit = value;
  }
  get unit(): string | undefined {
    return this.attributes.unit;
  }

  set salespriceUnit(value: string) {
    this.attributes.salespriceUnit = value;
  }
  get salespriceUnit(): string | undefined {
    return this.attributes.salespriceUnit;
  }

  /**
   *
   * Supplying the prices is optional. If not supplied, the prices will be fetched from the API.
   *
   * @param attributes  The attributes for the price collection
   * @param prices  The prices for the price collection
   */
  constructor(attributes: PriceAttributes, prices?: PriceObject) {
    this.attributes = attributes;

    // console.log(
    //   'prices',
    //   !!prices,
    //   'not null',
    //   Object.values(prices).some((price) => price !== null)
    // );
    if (prices && (Object.values(prices) as Price[]).some((price) => price !== null)) {
      this.prices = prices;
    } else {
      this.populate();
    }
  }

  // Cleans up the PriceManager to avoid memory leaks
  destroy(): void {
    window._priceManagerPriceStore.delete(this.key);
  }

  private populate(type?: PriceType): Promise<void> {
    let res: Promise<AxiosResponse<any, any>>;
    res = PromiseCache.get(() => {
      switch (type) {
        // TODO: Implement support for juts updating a single price type, if needed
        default:
          res = instance.get(`/pricecollection/${this.artnr}`, {
            params: {
              address: this.address,
              customer: this.customer,
              warehouse: this.warehouse,
              region: this.region,
              quantity: this.price_quantity ?? this.quantity,
              unit: this.unit,
              single: true,
              salespriceUnit: this.salespriceUnit,
            },
          });
          break;
      }

      return res;
    }, this.key);

    this._fetching = res;
    return new Promise<void>((resolve) => {
      res
        .then((response) => {
          if (response?.status === 200) {
            const data = response.data as {
              price_net: Price;
              price_sales: Price;
              price_line: LinePrice;
              price_m2?: LinePrice;
              // price_unit?: LinePrice;
            };
            switch (type) {
              case PriceType.Sales:
                this.prices.sales = data.price_sales;
                break;
              case PriceType.Net:
                this.prices.net = data.price_net;
                break;
              case PriceType.M2:
                this.prices.m2 = data.price_m2 ?? null;
                break;
              case PriceType.Line:
                this.prices.line = data.price_line;
                break;
              default:
                this.prices = {
                  sales: data.price_sales,
                  net: data.price_net,
                  line: data.price_line,
                  m2: data.price_m2 ?? null,
                };
            }
          }
          resolve();
        })
        .catch((error) => {
          console.error('Error fetching price collection', error);
          resolve();
        })
        .finally(() => {
          console.log('PriceManager: Fetching finished');
          this._fetching = null;
        });
    });
  }

  exists(type: PriceType): boolean {
    switch (type) {
      case PriceType.Sales:
        return this.prices.sales !== null;
      case PriceType.Net:
        return this.prices.net !== null;
      case PriceType.M2:
        return this.prices.m2 !== null;
      case PriceType.Line:
        return this.prices.line !== null;
      default:
        return false;
    }
  }

  getPrice(type: PriceType): Pricing {
    let amount: number | undefined;
    switch (type) {
      case PriceType.Net:
        amount = this.prices.net?.netPrice;
        break;
      case PriceType.Sales:
        amount = this.prices.sales?.salesPrice;
        break;
      case PriceType.M2:
        amount = this.prices.m2?.salesPrice;
        break;
      case PriceType.Line:
        amount = this.prices.line?.salesPrice;
        break;
      default:
        throw new Error('Invalid price type');
    }

    return new Pricing(amount ?? null, this.getCurrency(type));
  }

  getPriceTotal(type: PriceType): Pricing {
    let amount: number | undefined;
    switch (type) {
      case PriceType.Net:
        amount = this.prices.net?.price_total;
        break;
      case PriceType.Sales:
        amount = this.prices.sales?.price_total;
        break;
      case PriceType.M2:
        amount = this.prices.m2?.lineAmount;
        break;
      case PriceType.Line:
        amount = this.prices.line?.lineAmount;
        break;
      default:
        throw new Error('Invalid price type');
    }

    return new Pricing(amount ?? null, this.getCurrency(type));
  }

  getDiscount(
    type: Exclude<PriceType, PriceType.Net | PriceType.Sales>,
    index: 1 | 2 | 3 | 4 | 5 | 6 = 1
  ): { discount: number; text: string; status: string } | null {
    if (index < 1 || index > 6) {
      return null;
    }

    let linePrice: LinePrice | null = null;
    switch (type) {
      case PriceType.M2:
        linePrice = this.prices.m2;
        break;
      case PriceType.Line:
        linePrice = this.prices.line;
        break;
      default:
        return null;
    }

    return {
      discount: +(linePrice?.[`discount${index}`] ?? 0),
      text: linePrice?.[`discountText${index}`] ?? '',
      status: linePrice?.[`statusDiscount${index}`] ?? '',
    };
  }

  getAllDiscounts(
    type: Exclude<PriceType, PriceType.Net | PriceType.Sales>
  ): { discount: number; text: string; status: string }[] {
    const discounts: { discount: number; text: string; status: string }[] = [];
    for (let i = 1; i <= 6; i++) {
      const discount = this.getDiscount(type, i as 1 | 2 | 3 | 4 | 5 | 6);
      if (discount) {
        discounts.push(discount);
      }
    }

    return discounts;
  }

  getBaseUnit(type: PriceType): string | null {
    switch (type) {
      case PriceType.Net:
        return this.prices.net?.netPriceUnitOfMeasure ?? this.prices.net?.salesPriceUnitOfMeasure ?? null;
      case PriceType.Sales:
        return this.prices.sales?.salesPriceUnitOfMeasure ?? null;
      case PriceType.M2:
        return this.prices.m2?.priceUnitOfMeasure ?? this.prices.m2?.salesPriceUnitOfMeasure ?? null;
      case PriceType.Line:
        return this.prices.m2?.priceUnitOfMeasure ?? this.prices.line?.salesPriceUnitOfMeasure ?? null;
      default:
        return null;
    }
  }

  getCurrency(type: PriceType): Currency | null {
    switch (type) {
      case PriceType.Net:
        return toCurrency(this.prices.net?.currency ?? null);
      case PriceType.Sales:
        return toCurrency(this.prices.sales?.currency ?? null);
      case PriceType.M2:
        return toCurrency(this.prices.m2?.currency ?? null);
      case PriceType.Line:
        return toCurrency(this.prices.line?.currency ?? null);
      default:
        return null;
    }
  }

  getCurrencyWithUnit(type: PriceType): string {
    const currency = this.getCurrency(type);
    const unit = this.getBaseUnit(type);

    return `${currency}/${unit}`;
  }

  getAttributes(): PriceAttributes {
    return this.attributes;
  }
}

export function toCurrency(currency: Currency | string | null): Currency | null {
  if (currency !== null && currency in Currency) {
    return Currency[currency as keyof typeof Currency];
  }

  return null;
}

function toAmount(amount: number | null): number | null {
  if (amount === null || typeof amount !== 'number') {
    return null;
  }

  return amount;
}

const _precision = 4;
function newDinero(amount: number | null, currency: Currency | null): DineroType {
  let cents = 0;
  amount = toAmount(amount);
  if (amount !== null) {
    cents = Math.floor(amount * Math.pow(10, _precision));
  }
  return Dinero({
    amount: cents,
    currency: toCurrency(currency as string) || undefined,
    precision: _precision,
  });
}

export class Pricing {
  private d: DineroType = Dinero({ precision: _precision });
  private hasValue: boolean = false;

  set value(amount: number) {
    const currency = this.getCurrency();
    if (currency === null) {
      console.warn("Couldn't set price value because the currency is not recognized.");
      return;
    }
    this.set(amount, currency);
  }
  get value(): number | null {
    if (!this.hasValue) {
      return null;
    }

    return this.d.toUnit();
  }

  /**
   * Creates an instance of the Price class.
   *
   * @param value - The monetary value in major currency units (e.g., 100.50 for 100 dollars and 50 cents).
   * @param currency - The currency of the monetary value (e.g., Currency.USD).
   *
   * @throws Error if the currency is null or undefined.
   *
   * Note: Dinero.js expects the amount in minor currency units (e.g., cents).
   * Therefore, the value is multiplied by 100 to convert from major currency units to minor units.
   * For example, 100.50 (dollars) wcurrencyill be converted to 10050 (cents).
   */
  constructor(amount: number | null, currency: Currency | string | null) {
    // check if amount is a number, otherwise set it to null
    amount = toAmount(amount);
    const _currency = toCurrency(currency as string);

    if (amount !== null && _currency !== null) {
      this.set(amount, _currency);
    }
  }

  set(amount: number, currency: Currency): void {
    this.d = newDinero(amount, currency);
    this.hasValue = true;
  }

  clear(): void {
    this.d = newDinero(null, null);
    this.hasValue = false;
  }

  add(amount: Pricing | number): Pricing | void {
    if (!this.hasValue) return;
    const d = amount instanceof Pricing ? amount.d : newDinero(amount, this.getCurrency());
    try {
      if (d.getCurrency() !== this.d.getCurrency()) {
        return;
      }
      return new Pricing(this.d.add(d).toUnit(), this.getCurrency());
    } catch (e) {
      console.warn('Error adding prices', e);
      return;
    }
  }

  subtract(amount: Pricing | number): Pricing | void {
    if (!this.hasValue) return;
    const d = amount instanceof Pricing ? amount.d : newDinero(amount, this.getCurrency());
    return new Pricing(this.d.subtract(d).toUnit(), this.getCurrency());
  }

  multiply(amount: Pricing | number): Pricing | void {
    if (!this.hasValue) return;
    if (amount instanceof Pricing) {
      return new Pricing(this.d.multiply(amount.value || 1).toUnit(), this.getCurrency());
    }
    return new Pricing(this.d.multiply(amount).toUnit(), this.getCurrency());
  }

  divide(amount: Pricing | number): Pricing | void {
    if (!this.hasValue) return;
    if (amount instanceof Pricing) {
      return new Pricing(this.d.divide(amount.value || 1).toUnit(), this.getCurrency());
    }
    return new Pricing(this.d.divide(amount).toUnit(), this.getCurrency());
  }

  /**
   * Formats the price as a string.
   */
  toString(withCurrency: boolean = false): string {
    if (!this.hasValue) {
      return '';
    }

    let value = formatCurrency(this.value);

    if (withCurrency) {
      value += ' ' + this.d.getCurrency();
    }

    return value;
  }

  withDiscounts(
    discounts: {
      discount: number;
    }[] = []
  ): Pricing {
    let price = this.d;
    for (const discount of discounts) {
      const discount_amount = price.multiply(discount.discount / 100);
      price = price.subtract(discount_amount);
    }

    return new Pricing(price.toUnit(), this.getCurrency());
  }

  /**
   *
   * The currency of the monetary value (e.g., Currency.USD).
   * If the currency is not recognized, null is returned.
   */
  getCurrency(): Currency | null {
    return toCurrency(this.d.getCurrency());
  }

  asDinero(): DineroType {
    return this.d;
  }
}
